import {
    CalendarOutlined,
    CloudUploadOutlined,
    DollarOutlined,
    FileOutlined,
    LineChartOutlined,
    MailOutlined,
    MoonOutlined,
    PlusOutlined,
    StarOutlined,
    SunOutlined,
    TeamOutlined,
    UnorderedListOutlined,
    WalletOutlined
} from '@ant-design/icons'
import { usePermissions } from '@app/providers/router/model/hooks/use-permissions'
import { RoutePermission } from '@app/providers/router/model/types'
import { Theme, useTheme } from '@app/providers/theme-provider'
import { AddPostModal } from '@entities/post'
import logo from '@shared/assets/logo.svg'
import type { MenuProps } from 'antd'
import { Button, Image, Layout, Menu, Segmented, theme } from 'antd'
import React, { memo, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as styles from './sidebar.module.scss'

const { Sider } = Layout

type MenuItem = Required<MenuProps>['items'][number]

const buildNumber = process.env.REACT_APP_BUILD_NUMBER

export const Sidebar = memo(() => {
    const [openKeys, setOpenKeys] = useState(['1'])
    const [collapsed, setCollapsed] = useState(false)
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const navigate = useNavigate()

    const onOpenChange: MenuProps['onOpenChange'] = (keys) => {
        const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1)
        if (latestOpenKey) {
            setOpenKeys([latestOpenKey])
        } else {
            setOpenKeys([])
        }
    }

    const {
        token: { colorHighlight, colorBgContainer }
    } = theme.useToken()

    const { theme: appTheme, toggleTheme } = useTheme()
    const { hasPermission } = usePermissions()

    const onOpenAddPostModal = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        setIsCreateModalOpen(true)
    }

    const items: MenuItem[] = useMemo(() => {
        const baseItems: MenuItem[] = [
            {
                label: 'Интернет-ресурсы',
                key: '2',
                onClick: () => navigate('/internet-resources'),
                icon: <UnorderedListOutlined />,
                disabled: !hasPermission(RoutePermission.CHANNELS)
            },
            {
                label: (
                    <span style={{ verticalAlign: 'center' }}>
                        Постинг{' '}
                        <Button
                            onClick={onOpenAddPostModal}
                            style={{ marginLeft: 10, padding: '10px 20px' }}
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                        />
                    </span>
                ),
                key: 'posting',
                icon: <MailOutlined />,
                disabled: !hasPermission(RoutePermission.POSTING),
                children: [
                    {
                        style: { paddingLeft: 25 },
                        label: 'Черновики',
                        key: 'draft',
                        icon: <FileOutlined />,
                        onClick: () => navigate('/posts/draft')
                    },
                    {
                        style: { paddingLeft: 25 },
                        label: 'Запланированные',
                        icon: <CalendarOutlined />,
                        key: 'scheduled',
                        onClick: () => navigate('/posts/scheduled')
                    },
                    {
                        style: { paddingLeft: 25 },
                        label: 'Опубликованные',
                        icon: <CloudUploadOutlined />,
                        key: 'published',
                        onClick: () => navigate('/posts/published')
                    }
                ]
            },
            {
                label: 'Рекламный постинг',
                key: 'advertising',
                icon: <TeamOutlined />,
                disabled: !hasPermission(RoutePermission.ADVERTISING),
                children: [
                    {
                        label: 'Партнеры',
                        key: 'partners',
                        icon: <StarOutlined />,
                        disabled: !hasPermission(RoutePermission.ADVERTISING_PARTNERS),
                        onClick: () => navigate('/advertising/partners')
                    },
                    {
                        label: 'Закуп',
                        key: 'buy',
                        icon: <WalletOutlined />,
                        disabled: !hasPermission(RoutePermission.ADVERTISING_BUY),
                        onClick: () => navigate('/advertising/buy')
                    },
                    {
                        label: 'Продажа',
                        icon: <DollarOutlined />,
                        key: 'sell',
                        disabled: !hasPermission(RoutePermission.ADVERTISING_SELL),
                        onClick: () => navigate('/advertising/sell')
                    }
                ]
            },
            {
                label: 'Статистика',
                key: 'statistics',
                icon: <LineChartOutlined />,
                disabled: !hasPermission(RoutePermission.STATISTICS),
                onClick: () => navigate('/statistics')
            }
            // {
            //     label: 'Биржа',
            //     key: 'stock',
            //     icon: <TeamOutlined />,
            //     disabled: !hasPermission(RoutePermission.ADVERTISING),
            //     children: [
            //         {
            //             label: 'Купить рекламу',
            //             key: '/stock/buy-advertising',
            //             icon: <WalletOutlined />,
            //             disabled: !hasPermission(RoutePermission.ADVERTISING_PARTNERS),
            //             onClick: () => navigate('/stock/buy-advertising')
            //         },
            //         {
            //             label: 'Избранное',
            //             key: 'buy',
            //             icon: <StarOutlined />,
            //             disabled: !hasPermission(RoutePermission.ADVERTISING_BUY),
            //             onClick: () => navigate('/stock/favorites')
            //         },
            //         {
            //             label: '0 Каналов в корзине',
            //             icon: <DeleteOutlined />,
            //             key: 'sell',
            //             disabled: !hasPermission(RoutePermission.ADVERTISING_SELL),
            //             onClick: () => navigate('/stock/basket')
            //         }
            //     ]
            // }
            // {
            //     label: 'Реферальная программа',
            //     key: 'referral',
            //     icon: <FireOutlined />,
            //     disabled: false,
            //     onClick: () => navigate('/referral')
            // }
        ]

        return baseItems
    }, [hasPermission, navigate])

    return (
        <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={(value) => setCollapsed(value)}
            className={styles.sidebar}
            style={{ backgroundColor: appTheme === Theme.DARK ? colorBgContainer : colorHighlight }}
        >
            <div style={{ padding: '16px' }}>
                <Link
                    to="/"
                    style={{
                        display: 'block',
                        padding: collapsed ? '4px' : '16px',
                        borderRadius: '6px',
                        background:
                            appTheme === Theme.LIGHT
                                ? 'linear-gradient(to top, #DDE8EC, #7C9BAD)'
                                : 'linear-gradient(to top, #1f1f1f, #7C9BAD)'
                    }}
                >
                    <Image src={logo} preview={false} />
                </Link>
            </div>
            <Menu
                items={items}
                openKeys={openKeys}
                onOpenChange={onOpenChange}
                mode="inline"
                defaultSelectedKeys={['1']}
            />
            {/* <Popover title={user?.isAnonymous ? 'Для доступа к закрытым разделам необходима регистрация' : ''}>
                <Menu items={items} mode="inline" defaultSelectedKeys={['1']} />
            </Popover> */}
            <Segmented
                value={appTheme}
                style={{ margin: 'auto auto 16px auto' }}
                onChange={(value) => toggleTheme(value)}
                options={[
                    { value: Theme.LIGHT, icon: <SunOutlined /> },
                    { value: Theme.DARK, icon: <MoonOutlined /> }
                ]}
            />
            {/* {buildNumber && `Build: ${buildNumber}`} */}
            v.14.10.2025
            {isCreateModalOpen && <AddPostModal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />}
        </Sider>
    )
})
